/* CUSTOM: AI badge on AI-generated images — adds or removes a badge button on every container whose visible image carries the _ki filename marker, kept in sync via MutationObserver.
   Loaded globally from layout/theme.liquid; the modal and the localized badge title come from snippets/custom-global-ai-badge-modal.liquid. */
(() => {
  const MODAL_ID = 'custom-ai-badge-modal';
  const AI_MARKER = /_ki[_.]/;

  // Do not localize the badge text here: .js.liquid assets render once in the shop default language
  // (`| t` always yields EN), so read the title from the per-page localized modal snippet instead.
  function getBadgeTitle() {
    return document.getElementById(MODAL_ID)?.dataset.badgeTitle || 'AI-Generated';
  }

  // Excluded: gallery thumbnails, cart drawer, and cart page (no badge in the cart).
  const EXCLUDE_SELECTOR = '.product-gallery__thumbnail-list, .product-gallery__thumbnail-scroller, cart-drawer, .cart-page';

  // Finds the container the badge is anchored to — side-effect free.
  function findContainer(img) {
    const parent = img.parentElement;
    if (!parent) return null;
    // A picture element with display:contents has no box of its own — use its parent; a picture
    // element with a normal box (e.g. in image-with-text) is the container itself.
    const node = parent.tagName === 'PICTURE'
      ? (getComputedStyle(parent).display === 'contents' ? parent.parentElement : parent)
      : parent;
    return node || null;
  }

  // Target state: badge when the container is marked explicitly or shows an AI image. On product
  // cards only the primary image counts — the aria-hidden hover image must not trigger the badge.
  function containerShouldHaveBadge(container) {
    if (container.hasAttribute('data-ai-badge')) return true;
    const primary = container.querySelector('.product-card__image--primary');
    const imgs = primary ? [primary] : Array.from(container.querySelectorAll('img'));
    return imgs.some(
      (img) => !img.closest(EXCLUDE_SELECTOR) && AI_MARKER.test(img.src)
    );
  }

  function ensureBadge(container) {
    if (container.querySelector(':scope > .custom-ai-badge')) return;
    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'custom-ai-badge';
    btn.setAttribute('aria-controls', MODAL_ID);
    btn.setAttribute('aria-expanded', 'false');
    const span = document.createElement('span');
    span.textContent = getBadgeTitle();
    btn.appendChild(span);
    container.appendChild(btn);
  }

  function removeBadge(container) {
    const btn = container.querySelector(':scope > .custom-ai-badge');
    if (btn) btn.remove();
  }

  // Restores the target state (add OR remove) — idempotent.
  function reconcile(container) {
    if (!container) return;
    if (containerShouldHaveBadge(container)) {
      ensureBadge(container);
    } else {
      removeBadge(container);
    }
  }

  function reconcileForImg(img) {
    if (img.closest(EXCLUDE_SELECTOR)) return;
    reconcile(findContainer(img));
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('img').forEach(reconcileForImg);
    document.querySelectorAll('[data-ai-badge]').forEach(reconcile);

    // Scrollbar compensation: prevents layout shift when the modal opens.
    const modal = document.getElementById(MODAL_ID);
    if (modal) {
      modal.addEventListener('dialog:before-show', () => {
        const w = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--modal-scrollbar-compensation', `${w}px`);
      });
      modal.addEventListener('dialog:after-hide', () => {
        document.documentElement.style.removeProperty('--modal-scrollbar-compensation');
      });
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          // src/srcset swap on the same img element (contexts without node replacement).
          if (mutation.target.tagName === 'IMG') reconcileForImg(mutation.target);
          continue;
        }
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            reconcileForImg(node);
          } else {
            node.querySelectorAll?.('img').forEach(reconcileForImg);
            if (node.hasAttribute?.('data-ai-badge')) reconcile(node);
            node.querySelectorAll?.('[data-ai-badge]').forEach(reconcile);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'srcset']
    });
  });
})();
